/**
 * PageCustomTemplate — Detail page for a single custom template (spreadsheet upload)
 *
 * Loaded via route: /project/:id/spend-plans/custom-templates/:templateId
 * Uses HeaderDetailPage for consistent detail page styling.
 * Shows a loading skeleton while fetching large JSON payloads from the API.
 */

// react
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

// router
import { useParams } from 'react-router-dom';

// i18n

// context
import { ContextBanner } from '../../../../App.context';
import { ContextProject } from '../../Project/Project.context';

// mui
import {
	alpha,
	Box,
	Button,
	Chip,
	CircularProgress,
	Collapse,
	Paper,
	Skeleton,
	Stack,
	Typography,
	useTheme,
} from '@mui/material';

// icons
import {
	Description as IconFile,
	Save as IconSave,
	TableChart as IconTable,
} from '@mui/icons-material';

// components
import { HeaderDetailPage } from '../../../ui/HeaderDetailPage/HeaderDetailPage';
import { ExcelStyledTable } from './ExcelStyledTable';

// types
import { StyledRow, SheetMetadata } from './parseExcelStyled';

// Types

interface ExcelUploadRecord {
	id: number;
	projectId: number;
	name: string;
	description: string | null;
	fileName: string | null;
	sheetName: string | null;
	documentType: string;
	headers: string[];
	rows: StyledRow[];
	metadata: SheetMetadata;
	linkedEntityType: string | null;
	linkedEntityId: number | null;
	status: string;
	rowCount?: number;
	createdBy: number | null;
	modifiedBy: number | null;
	created: string;
	modified: string | null;
}

// Document type labels
const DOCUMENT_TYPES: Record<string, string> = {
	general: 'General',
	annual_work_plan: 'Annual Work Plan',
	procurement_plan: 'Procurement Plan',
	activity_plan: 'Activity Plan',
	budget: 'Budget',
	other: 'Other',
};

// Main Component

export const PageCustomTemplate = () => {
	const theme = useTheme();
	const { templateId } = useParams<{ templateId: string }>();
	const { project } = useContext(ContextProject);
	const { setBanner } = useContext(ContextBanner);

	// State
	const [record, setRecord] = useState<ExcelUploadRecord | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isRendering, setIsRendering] = useState(false);
	const [editedRows, setEditedRows] = useState<StyledRow[]>([]);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	// API URL
	const apiUrl =
		process.env.REACT_APP_API_URL?.replace(/\/v1\/?$/, '') || process.env.REACT_APP_API_URL;

	// Auth headers
	const getHeaders = useCallback((includeContentType = true) => {
		const accessToken = sessionStorage.getItem('accessToken');
		const idToken = sessionStorage.getItem('idToken');
		const headers: Record<string, string> = {
			Authorization: `Bearer ${accessToken}`,
			User: String(idToken),
		};
		if (includeContentType) {
			headers['Content-Type'] = 'application/json';
		}
		return headers;
	}, []);

	// Back path
	const backPath = useMemo(() => {
		if (!project?.idWbg) return '../custom-templates';
		return `/project/${project.idWbg}/spend-plans/custom-templates`;
	}, [project?.idWbg]);

	// ==========================================================================
	// Fetch record
	// ==========================================================================

	useEffect(() => {
		if (!templateId) return;

		const fetchRecord = async () => {
			setIsLoading(true);
			try {
				const res = await fetch(`${apiUrl}/v2/spreadsheet-data/${templateId}`, {
					headers: getHeaders(false),
				});
				const json = await res.json();

				if (json.error) {
					throw new Error(json.error.message);
				}

				setRecord(json.data);

				// Defer row rendering for large datasets to avoid blocking the UI
				setIsRendering(true);
				requestAnimationFrame(() => {
					setEditedRows(json.data.rows || []);
					setIsRendering(false);
				});
			} catch (err) {
				setBanner({
					message: `Failed to load template: ${err instanceof Error ? err.message : err}`,
					severity: 'error',
				});
			} finally {
				setIsLoading(false);
			}
		};

		fetchRecord();
	}, [apiUrl, getHeaders, templateId, setBanner]);

	// ==========================================================================
	// Cell editing
	// ==========================================================================

	const handleCellChange = useCallback((rowIndex: number, col: string, newValue: string) => {
		setEditedRows((prev) =>
			prev.map((row) => {
				if (row.rowIndex !== rowIndex) return row;
				return {
					...row,
					cells: {
						...row.cells,
						[col]: {
							...row.cells[col],
							value: newValue,
							col,
						},
					},
				};
			})
		);
		setHasUnsavedChanges(true);
	}, []);

	// ==========================================================================
	// Save edits
	// ==========================================================================

	const handleSaveEdits = useCallback(async () => {
		if (!record) return;
		setIsSaving(true);

		try {
			const res = await fetch(`${apiUrl}/v2/spreadsheet-data/${record.id}`, {
				method: 'PUT',
				headers: getHeaders(),
				body: JSON.stringify({ rows: editedRows }),
			});
			const json = await res.json();

			if (json.error) throw new Error(json.error.message);

			setBanner({
				message: 'Changes saved successfully.',
				severity: 'success',
			});
			setHasUnsavedChanges(false);
			setRecord(json.data);
		} catch (err) {
			setBanner({
				message: `Failed to save: ${err instanceof Error ? err.message : err}`,
				severity: 'error',
			});
		} finally {
			setIsSaving(false);
		}
	}, [apiUrl, editedRows, getHeaders, record, setBanner]);

	// ==========================================================================
	// Metadata
	// ==========================================================================

	const recordMetadata: SheetMetadata = useMemo(() => {
		if (!record) {
			return {
				mergedCells: [],
				columnWidths: {},
				hiddenColumns: [],
				totalColumns: 0,
				totalRows: 0,
				columnOrder: [],
			};
		}
		return (
			record.metadata || {
				mergedCells: [],
				columnWidths: {},
				hiddenColumns: [],
				totalColumns: record.headers?.length || 0,
				totalRows: editedRows.length,
				columnOrder: record.headers || [],
			}
		);
	}, [record, editedRows.length]);

	// ==========================================================================
	// Render
	// ==========================================================================

	return (
		<Box sx={{ minHeight: '100%', bgcolor: 'background.default' }}>
			{/* Detail Header */}
			<HeaderDetailPage
				icon={IconTable}
				breadcrumb={{
					label: 'Custom Templates',
					to: backPath,
				}}
				title={record?.name || 'Loading...'}
				status={record?.status}
				variant="workPlan"
				loading={isLoading}
				stats={
					record
						? [
								{ label: 'Columns', value: record.headers?.length || 0 },
								{ label: 'Rows', value: editedRows.length },
								{
									label: 'Type',
									value: DOCUMENT_TYPES[record.documentType] || record.documentType,
								},
								{
									label: 'Created',
									value: new Date(record.created).toLocaleDateString(),
								},
							]
						: undefined
				}
				loadingActions={1}
				customAction={
					!isLoading ? (
						<Button
							variant="contained"
							startIcon={isSaving ? <CircularProgress size={18} /> : <IconSave />}
							onClick={handleSaveEdits}
							disabled={!hasUnsavedChanges || isSaving}
							color={hasUnsavedChanges ? 'primary' : 'inherit'}>
							{isSaving ? 'Saving...' : hasUnsavedChanges ? 'Save Changes' : 'Saved'}
						</Button>
					) : undefined
				}
			/>

			{/* Unsaved changes banner */}
			{hasUnsavedChanges && (
				<Box
					sx={{
						px: 3,
						py: 1,
						bgcolor: alpha(theme.palette.warning.main, 0.1),
						borderBottom: '1px solid',
						borderColor: alpha(theme.palette.warning.main, 0.3),
					}}>
					<Typography variant="body2" color="warning.dark" fontWeight={500}>
						You have unsaved changes. Click &quot;Save Changes&quot; to persist your edits.
					</Typography>
				</Box>
			)}

			{/* Content */}
			<Box sx={{ p: 3 }}>
				{/* Loading skeleton for data */}
				{isLoading && (
					<Box>
						<Stack direction="row" spacing={1} sx={{ mb: 2 }}>
							<Skeleton variant="rounded" width={100} height={24} />
							<Skeleton variant="rounded" width={140} height={24} />
							<Skeleton variant="rounded" width={120} height={24} />
						</Stack>
						<Skeleton variant="rounded" height={400} />
					</Box>
				)}

				{/* Rendering large dataset indicator */}
				{!isLoading && isRendering && (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							py: 12,
						}}>
						<CircularProgress size={48} sx={{ mb: 2 }} />
						<Typography variant="h6" color="text.secondary">
							Rendering spreadsheet...
						</Typography>
						<Typography variant="body2" color="text.disabled">
							Large files may take a moment to display.
						</Typography>
					</Box>
				)}

				{/* Loaded content */}
				{!isLoading && !isRendering && record && (
					<>
						{/* Info chips */}
						<Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
							<Chip
								label={DOCUMENT_TYPES[record.documentType] || record.documentType}
								size="small"
								variant="outlined"
							/>
							{record.fileName && (
								<Chip icon={<IconFile />} label={record.fileName} size="small" variant="outlined" />
							)}
							{record.sheetName && (
								<Chip label={`Sheet: ${record.sheetName}`} size="small" variant="outlined" />
							)}
							{recordMetadata.mergedCells.length > 0 && (
								<Chip
									label={`${recordMetadata.mergedCells.length} merged regions`}
									size="small"
									variant="outlined"
									color="info"
								/>
							)}
							{record.modified && (
								<Chip
									label={`Modified ${new Date(record.modified).toLocaleDateString()}`}
									size="small"
									variant="outlined"
								/>
							)}
						</Stack>

						{/* Styled Table — Editable */}
						<ExcelStyledTable
							rows={editedRows}
							columns={record.headers || []}
							metadata={recordMetadata}
							editable={true}
							onCellChange={handleCellChange}
							maxHeight={650}
						/>

						{/* JSON Preview */}
						<JsonPreview rows={editedRows} metadata={recordMetadata} />
					</>
				)}
			</Box>
		</Box>
	);
};

// JSON Preview Component

const JsonPreview = ({ rows, metadata }: { rows: StyledRow[]; metadata: SheetMetadata }) => {
	const [isOpen, setIsOpen] = useState(false);

	const previewData = useMemo(() => {
		return {
			metadata: {
				mergedCells: metadata.mergedCells,
				totalRows: metadata.totalRows,
				totalColumns: metadata.totalColumns,
			},
			rows: rows.slice(0, 5).map((r) => ({
				rowIndex: r.rowIndex,
				cells: Object.fromEntries(
					Object.entries(r.cells).map(([col, cell]) => [
						col,
						{
							value: cell.value,
							...(cell.style ? { style: cell.style } : {}),
							...(cell.colSpan ? { colSpan: cell.colSpan } : {}),
							...(cell.rowSpan ? { rowSpan: cell.rowSpan } : {}),
						},
					])
				),
			})),
		};
	}, [rows, metadata]);

	return (
		<Box sx={{ mt: 3 }}>
			<Button variant="text" size="small" onClick={() => setIsOpen(!isOpen)} sx={{ mb: 1 }}>
				{isOpen ? 'Hide' : 'Show'} JSON Preview ({rows.length} rows)
			</Button>
			<Collapse in={isOpen}>
				<Paper
					sx={{
						p: 2,
						maxHeight: 400,
						overflow: 'auto',
						bgcolor: '#1e1e1e',
						borderRadius: 2,
					}}>
					<pre
						style={{
							margin: 0,
							fontFamily: 'monospace',
							fontSize: '0.8rem',
							color: '#d4d4d4',
							whiteSpace: 'pre-wrap',
							wordBreak: 'break-all',
						}}>
						{JSON.stringify(previewData, null, 2)}
						{rows.length > 5 && (
							<span style={{ color: '#6a9955' }}>
								{`\n\n// ... and ${rows.length - 5} more rows`}
							</span>
						)}
					</pre>
				</Paper>
			</Collapse>
		</Box>
	);
};
